import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {Link, useParams} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {gql} from '@apollo/client';
import {Helmet} from 'react-helmet-async';

import Error from '../../component/error/Error';
import Loading from '../../component/loading/Loading';
import {SCHEMA_CONTEXT, sellerSchemaOrg} from '../../util/schemaOrg';
import {seoURIComponent} from '../../util/text';
import {sellerUrl, webappURL} from '../../util/url';

import Like from './like/Like';
import Profile from './profile/Profile';
import Review from './review/Review';
import Shop from './shop/Shop';
import SellerOpenGraph from './SellerOpenGraph';
import SellerTwitterCard from './SellerTwitterCard';
import NotFound from '../notFound/NotFound';

import './Seller.css';
import {isWithinInt32Range} from "../../util/util";
import SellerSearchInput from './SellerSearchInput';

import {SELLER_TAB} from './sellerConstants';
export {SELLER_TAB};

// GraphQL queries
const SEE_USER = gql`
    query SeeUser($userId: Int!) {
        seeUser(id: $userId) {
            id
            username
            nickname
            bio
            resizedBigImage
            resizedBigCoverImage
            follower_count
            following_count
            seller {
                id
                rating
                review_count
                productCount
                is_certified
            }
        }
    }
`;

/**
 * Return a Korean description for a seller page tab.
 * @param {Symbol} sellerTab the tab on the seller page
 * @returns a description in Korean
 */
export function getSellerTabToKorean(sellerTab) {
    switch (sellerTab) {
        case SELLER_TAB.SHOP:
            return {
                title :'의 Shop',
                description: '의 상품을 후루츠에서 거래하세요.'
            }
        case SELLER_TAB.LIKE:
            return {
                title :'의 위시리스트',
                description: '의 후루츠 위시리스트를 구경해 보세요.'
            }
        case SELLER_TAB.REVIEW:
            return {
                title :' 상점의 리뷰',
                description: ' 상점에 남겨진 리뷰를 확인해 보세요.'
            }
        default:
            throw new Error('Unknown seller tab type.');
    }
}

/**
 * Seller page component.
 */
export default function Seller({ hostname, userAgent }) {
    // fetch the seller details
    const {
        sellerId: encodedSellerId,
        sellerTab: encodedSellerTab = seoURIComponent(SELLER_TAB.SHOP.description) // default parameter value
    } = useParams();
    const sellerId = parseInt(encodedSellerId, 36);
    const isValidSellerId = isWithinInt32Range(sellerId);

    const sellerTab = Object.values(SELLER_TAB)
        .find(symbol => encodedSellerTab === seoURIComponent(symbol.description));

    // fetch seller details
    const {loading, error, data} = useQuery(
        SEE_USER,
        {
            variables: {
                userId: isValidSellerId ? sellerId : 0
            },
            skip: !isValidSellerId,
        }
    );

    // 독립 검색 키워드 상태 (필터와 분리) - 로딩/에러 early return 이전에 선언하여 Hook 순서를 고정
    const [keywordInput, setKeywordInput] = useState('');
    const [keyword, setKeyword] = useState('');

    const [navStickyTop, setNavStickyTop] = useState(null);

    useEffect(() => {
        const navigationElement = document.querySelector('.Navigation-nav');
        const submenuElement = document.querySelector('.Navigation-submenu');

        if (!navigationElement) return undefined;

        const update = () => {
            const navBottom = navigationElement.getBoundingClientRect().bottom;
            const submenuHeight =
                submenuElement && window.getComputedStyle(submenuElement).display !== 'none'
                    ? submenuElement.getBoundingClientRect().height
                    : 0;
            setNavStickyTop(navBottom + submenuHeight);
        };

        update();

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(update);
            resizeObserver.observe(navigationElement);
            if (submenuElement) resizeObserver.observe(submenuElement);
        } else {
            window.addEventListener('resize', update);
        }

        return () => {
            if (resizeObserver) resizeObserver.disconnect();
            else window.removeEventListener('resize', update);
        };
    }, []);

    if (!isValidSellerId) {
        return <NotFound />;
    }

    if (loading) {
        return <Loading />;
    } else if (error) {
        return <Error error={error} />;
    }

    const seller = {
        ...data.seeUser.seller,
        reviews: new Array(data.seeUser.seller.review_count), // FIXME missing reviews field from SeeUser() api
        user: Object.fromEntries(
            Object.entries(data.seeUser)
                .filter(([key]) => key !== 'seller')
        )
    };

    // build the UI elements
    const tabContent = (() => {
        switch (sellerTab) {
            case SELLER_TAB.SHOP:
                return <Shop seller={seller} keyword={keyword} />;
            case SELLER_TAB.LIKE:
                return <Like seller={seller} />;
            case SELLER_TAB.REVIEW:
                return <Review seller={seller} />;
            default:
                throw new Error('Unknown seller tab type.');
        }
    })();

    // meta tag
    const metaTitle = `${seller.user.nickname}(@${seller.user.username})${getSellerTabToKorean(sellerTab).title} | 후루츠패밀리`;
    const metaDescription = (seller.user.bio ? `${seller.user.bio.trim()} | ` : '') + `${seller.user.username}${getSellerTabToKorean(sellerTab).description}`

    return (
        <section className="Seller container">
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={webappURL(hostname) + sellerUrl(seller, sellerTab)} />
                <script type="application/ld+json">{JSON.stringify(sellerSchemaOrg(seller, SCHEMA_CONTEXT))}</script>
            </Helmet>
            <SellerOpenGraph seller={seller} sellerTab={sellerTab} title={metaTitle} description={metaDescription} hostname={hostname}/>
            <SellerTwitterCard seller={seller} sellerTab={sellerTab} title={metaTitle} description={metaDescription} />

            <div className="Seller-details">
                <Profile hostname={hostname}
                         seller={seller}
                         userAgent={userAgent} />
            </div>

            <nav
                className="Seller-nav sticky-top"
                style={navStickyTop != null ? { top: `${navStickyTop}px` } : undefined}
            >
                <div className="Seller-nav-row">
                    <ul className="Seller-nav-underline nav nav-underline">
                        <li className="Seller-nav-item nav-item">
                            <Link className={`Seller-nav-link nav-link ${sellerTab === SELLER_TAB.SHOP ? 'active' : ''}`}
                                  to={sellerUrl(seller, SELLER_TAB.SHOP)}
                                  replace={true}>
                                상품
                            </Link>
                        </li>
                        <li className="Seller-nav-item nav-item">
                            <Link className={`Seller-nav-link nav-link ${sellerTab === SELLER_TAB.LIKE ? 'active' : ''}`}
                                  to={sellerUrl(seller, SELLER_TAB.LIKE)}
                                  replace={true}>
                                위시리스트
                            </Link>
                        </li>
                        <li className="Seller-nav-item nav-item">
                            <Link className={`Seller-nav-link nav-link ${sellerTab === SELLER_TAB.REVIEW ? 'active' : ''}`}
                                  to={sellerUrl(seller, SELLER_TAB.REVIEW)}
                                  replace={true}>
                                리뷰
                            </Link>
                        </li>
                    </ul>
                    {sellerTab === SELLER_TAB.SHOP && (
                        <div className="Seller-search Seller-search--desktop">
                            <SellerSearchInput
                                value={keywordInput}
                                onChange={(e) => setKeywordInput(e.target.value)}
                                onEnter={() => setKeyword(keywordInput.trim())}
                            />
                        </div>
                    )}
                </div>
            </nav>

            {sellerTab === SELLER_TAB.SHOP && (
                <div className="Seller-search Seller-search--mobile">
                    <SellerSearchInput
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onEnter={() => setKeyword(keywordInput.trim())}
                    />
                </div>
            )}

            {tabContent}
        </section>
    );
}
Seller.propTypes = {
    hostname: PropTypes.string.isRequired,
    userAgent: PropTypes.string.isRequired
};
